import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { ordersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, ChevronDown, ChevronRight, RefreshCw, Trash2, Cloud, CheckSquare } from 'lucide-react';

export const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('shipped');
  const [sortConfig, setSortConfig] = useState({ key: 'order_date', direction: 'desc' });
  const [tabValue, setTabValue] = useState('orders');

  const [newOrder, setNewOrder] = useState({
    supplier: '',
    order_date: new Date().toISOString().slice(0, 10),
    products: [{ ean: '', asin: '', quantity: 1, unit_price_eur: 0, weight_grams: null }],
    notes: '',
  });

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await ordersAPI.getAll();
      setOrders(response.data);
      setSelectedOrders(new Set());
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const toggleExpand = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const handleSyncQogita = async () => {
    setSyncing(true);
    try {
      const response = await ordersAPI.syncQogita();
      toast.success(response.data.message);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to sync from Qogita');
    } finally {
      setSyncing(false);
    }
  };

  const addProductRow = () => {
    setNewOrder({
      ...newOrder,
      products: [...newOrder.products, { ean: '', asin: '', quantity: 1, unit_price_eur: 0, weight_grams: null }],
    });
  };

  const updateProduct = (index, field, value) => {
    const products = [...newOrder.products];
    products[index][field] = value;
    setNewOrder({ ...newOrder, products });
  };

  const removeProduct = (index) => {
    if (newOrder.products.length > 1) {
      const products = newOrder.products.filter((_, i) => i !== index);
      setNewOrder({ ...newOrder, products });
    }
  };

  const handleCreateOrder = async () => {
    try {
      const orderData = {
        ...newOrder,
        status: 'shipped',
        products: newOrder.products.map(p => ({
          ...p,
          quantity: parseInt(p.quantity),
          unit_price_eur: parseFloat(p.unit_price_eur),
          weight_grams: p.weight_grams ? parseFloat(p.weight_grams) : null,
        })),
      };
      await ordersAPI.create(orderData);
      toast.success('Order created successfully');
      setDialogOpen(false);
      setNewOrder({
        supplier: '',
        order_date: new Date().toISOString().slice(0, 10),
        products: [{ ean: '', asin: '', quantity: 1, unit_price_eur: 0, weight_grams: null }],
        notes: '',
      });
      fetchOrders();
    } catch (error) {
      toast.error('Failed to create order');
    }
  };

  const handleUpdateStatus = async (orderId, status) => {
    try {
      await ordersAPI.updateStatus(orderId, status);
      toast.success('Status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await ordersAPI.delete(orderId);
      toast.success('Order deleted');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  const formatEuro = (value) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0);

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      shipped: 'badge-info',
      delivered: 'badge-success',
      cancelled: 'badge-danger',
    };
    return `badge ${badges[status] || 'badge-neutral'}`;
  };
  
  const getSourceBadge = (source) => {
    const sourceStyles = {
      qogita: 'badge-info',
      manual: 'badge-neutral',
    };
    const label = source === 'qogita' ? 'Qogita' : 'Manual';
    return (
      <span className={`badge ${sourceStyles[source] || 'badge-neutral'}`}>
        {label}
      </span>
    );
  };

  const getOrderIdDisplay = (order) => {
    if (order.source === 'qogita') {
      return order.supplier || order.external_id || `${order.id.slice(0, 8)}...`;
    }
    if (order.external_id) {
      return order.external_id;
    }
    return `${order.id.slice(0, 8)}...`;
  };

  const sortedOrders = useMemo(() => {
    const ordersCopy = [...orders];
    ordersCopy.sort((a, b) => {
      const { key, direction } = sortConfig;
      const factor = direction === 'asc' ? 1 : -1;

      const getValue = (order) => {
        switch (key) {
          case 'supplier':
            return order.source === 'qogita' ? 'Qogita' : order.supplier || '';
          case 'orderId':
            return getOrderIdDisplay(order);
          case 'order_date':
            return order.order_date || '';
          case 'source':
            return order.source || '';
          case 'products':
            return order.products?.length || 0;
          case 'total_cost':
            return order.total_cost || 0;
          case 'status':
            return order.status || '';
          default:
            return order.order_date || '';
        }
      };

      const valueA = getValue(a);
      const valueB = getValue(b);

      if (valueA < valueB) return -1 * factor;
      if (valueA > valueB) return 1 * factor;
      return 0;
    });
    return ordersCopy;
  }, [orders, sortConfig]);

  const flattenedLines = useMemo(() => {
    const rows = [];
    sortedOrders.forEach((order) => {
      const orderLink =
        order.source === 'qogita' && order.external_id
          ? `https://www.qogita.com/account/orders/${order.external_id}/`
          : null;
      const supplierName = order.source === 'qogita' ? 'Qogita' : order.supplier || '';

      (order.products || []).forEach((product, index) => {
        rows.push({
          id: `${order.id}-${index}`,
          date: order.order_date,
          name: product.name || product.ean || product.asin || 'Produit',
          supplier: supplierName,
          orderId: getOrderIdDisplay(order),
          ean: product.ean || '-',
          asin: product.asin || '-',
          link: orderLink,
          quantity: product.quantity,
          unitPrice: product.unit_price_eur,
        });
      });
    });
    return rows;
  }, [sortedOrders]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return '';
    }
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  const toggleSelectOrder = (orderId) => {
    const updated = new Set(selectedOrders);
    if (updated.has(orderId)) {
      updated.delete(orderId);
    } else {
      updated.add(orderId);
    }
    setSelectedOrders(updated);
  };

  const toggleSelectAll = () => {
    if (selectedOrders.size === sortedOrders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(sortedOrders.map((order) => order.id)));
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedOrders.size === 0) {
      toast.info('Select at least one order');
      return;
    }
    try {
      await Promise.all(
        Array.from(selectedOrders).map((orderId) =>
          ordersAPI.updateStatus(orderId, bulkStatus)
        )
      );
      toast.success(`Updated ${selectedOrders.size} orders to ${bulkStatus}`);
      setSelectedOrders(new Set());
      fetchOrders();
    } catch (error) {
      toast.error('Failed bulk update');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrders.size === 0) {
      toast.info('Select at least one order');
      return;
    }

    if (!window.confirm(`Delete ${selectedOrders.size} selected orders?`)) {
      return;
    }

    try {
      await Promise.all(
        Array.from(selectedOrders).map((orderId) => ordersAPI.delete(orderId))
      );
      toast.success(`Deleted ${selectedOrders.size} orders`);
      setSelectedOrders(new Set());
      fetchOrders();
    } catch (error) {
      toast.error('Failed bulk delete');
    }
  };

  return (
    <Layout>
      <Tabs value={tabValue} onValueChange={setTabValue} className="w-full">
        <div className="page-header flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="page-title" data-testid="orders-title">Orders</h1>
            <p className="page-subtitle">Manage supplier orders and track COGS</p>
          </div>
          <div className="flex flex-col gap-3 md:items-end">
            <TabsList>
              <TabsTrigger value="orders">Commandes</TabsTrigger>
              <TabsTrigger value="items">Produits</TabsTrigger>
            </TabsList>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                variant="outline"
                onClick={handleSyncQogita}
                disabled={syncing}
                data-testid="sync-qogita-btn"
              >
                <Cloud size={16} className="mr-2" />
                {syncing ? 'Syncing...' : 'Sync Qogita'}
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-order-btn">
                    <Plus size={16} className="mr-2" />
                    Create Order
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Order</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Supplier</Label>
                        <Input
                          value={newOrder.supplier}
                          onChange={(e) => setNewOrder({ ...newOrder, supplier: e.target.value })}
                          placeholder="Supplier name"
                          data-testid="order-supplier-input"
                        />
                      </div>
                      <div>
                        <Label>Order Date</Label>
                        <Input
                          type="date"
                          value={newOrder.order_date}
                          onChange={(e) => setNewOrder({ ...newOrder, order_date: e.target.value })}
                          data-testid="order-date-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Products</Label>
                      <div className="space-y-2 mt-2">
                        {newOrder.products.map((product, index) => (
                          <div key={index} className="grid grid-cols-6 gap-2 items-end">
                            <Input
                              placeholder="EAN"
                              value={product.ean}
                              onChange={(e) => updateProduct(index, 'ean', e.target.value)}
                              data-testid={`product-ean-${index}`}
                            />
                            <Input
                              placeholder="ASIN"
                              value={product.asin}
                              onChange={(e) => updateProduct(index, 'asin', e.target.value)}
                              data-testid={`product-asin-${index}`}
                            />
                            <Input
                              type="number"
                              placeholder="Qty"
                              value={product.quantity}
                              onChange={(e) => updateProduct(index, 'quantity', e.target.value)}
                              data-testid={`product-qty-${index}`}
                            />
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Price €"
                              value={product.unit_price_eur}
                              onChange={(e) => updateProduct(index, 'unit_price_eur', e.target.value)}
                              data-testid={`product-price-${index}`}
                            />
                            <Input
                              type="number"
                              placeholder="Weight (g)"
                              value={product.weight_grams || ''}
                              onChange={(e) => updateProduct(index, 'weight_grams', e.target.value)}
                              data-testid={`product-weight-${index}`}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeProduct(index)}
                              disabled={newOrder.products.length === 1}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <Button variant="outline" size="sm" className="mt-2" onClick={addProductRow}>
                        <Plus size={14} className="mr-1" /> Add Product
                      </Button>
                    </div>

                    <div>
                      <Label>Notes</Label>
                      <Input
                        value={newOrder.notes}
                        onChange={(e) => setNewOrder({ ...newOrder, notes: e.target.value })}
                        placeholder="Optional notes"
                        data-testid="order-notes-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateOrder} data-testid="submit-order-btn">Create Order</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>

        <TabsContent value="orders" className="space-y-4 mt-4">
          <Card>
            <CardContent className="p-4 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <CheckSquare size={18} />
                <p className="text-sm text-slate-600">
                  {selectedOrders.size} selected
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={handleBulkUpdate}>
                  Apply
                </Button>
                <Button variant="destructive" onClick={handleBulkDelete}>
                  Delete Selected
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="orders-table-card">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <input
                          type="checkbox"
                          checked={selectedOrders.size === sortedOrders.length && sortedOrders.length > 0}
                          onChange={toggleSelectAll}
                          className="accent-slate-900 h-4 w-4"
                          aria-label="Select all orders"
                        />
                      </TableHead>
                      <TableHead className="w-10"></TableHead>
                      <TableHead
                        className="cursor-pointer select-none"
                        onClick={() => handleSort('orderId')}
                      >
                        Order ID {getSortIndicator('orderId')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none"
                        onClick={() => handleSort('supplier')}
                      >
                        Supplier {getSortIndicator('supplier')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none"
                        onClick={() => handleSort('order_date')}
                      >
                        Date {getSortIndicator('order_date')}
                      </TableHead>
                  <TableHead>Source & Link</TableHead>
                      <TableHead
                        className="cursor-pointer select-none"
                        onClick={() => handleSort('products')}
                      >
                        Products {getSortIndicator('products')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none"
                        onClick={() => handleSort('total_cost')}
                      >
                        Total Cost {getSortIndicator('total_cost')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none"
                        onClick={() => handleSort('status')}
                      >
                        Status {getSortIndicator('status')}
                      </TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedOrders.map((order) => (
                      <React.Fragment key={order.id}>
                        <TableRow
                          className="collapsible-row"
                          onClick={() => toggleExpand(order.id)}
                          data-testid={`order-row-${order.id}`}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedOrders.has(order.id)}
                              onChange={() => toggleSelectOrder(order.id)}
                              className="accent-slate-900 h-4 w-4"
                              aria-label="Select order"
                            />
                          </TableCell>
                          <TableCell>
                            {expandedOrders.has(order.id) ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {getOrderIdDisplay(order)}
                          </TableCell>
                          <TableCell className="font-medium">
                            {order.source === 'qogita' ? 'Qogita' : order.supplier}
                          </TableCell>
                          <TableCell>{order.order_date}</TableCell>
                      <TableCell>
                        {order.source === 'qogita' && order.external_id ? (
                          <a
                            href={`https://www.qogita.com/account/orders/${order.external_id}/`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 underline text-sm"
                          >
                            Voir la commande
                          </a>
                        ) : (
                          <span className="text-xs text-slate-500">-</span>
                        )}
                      </TableCell>
                          <TableCell>{order.products.length} items</TableCell>
                          <TableCell className="font-medium">{formatCurrency(order.total_cost)}</TableCell>
                          <TableCell>
                            <span className={getStatusBadge(order.status)}>{order.status}</span>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <div className="flex gap-2">
                              <Select
                                value={order.status || 'shipped'}
                                onValueChange={(value) => handleUpdateStatus(order.id, value)}
                              >
                                <SelectTrigger className="w-28 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="shipped">Shipped</SelectItem>
                                  <SelectItem value="delivered">Delivered</SelectItem>
                                  <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteOrder(order.id)}
                                data-testid={`delete-order-${order.id}`}
                              >
                                <Trash2 size={16} className="text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedOrders.has(order.id) && (
                          <TableRow>
                            <TableCell colSpan={10} className="p-0">
                              <div className="collapsible-content p-4">
                                <p className="text-sm font-medium mb-2">Products in this order:</p>
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>EAN</TableHead>
                                      <TableHead>ASIN</TableHead>
                                      <TableHead>Quantity</TableHead>
                                      <TableHead>Unit Price (€)</TableHead>
                                      <TableHead>Weight (g)</TableHead>
                                      <TableHead>COGS ($)</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {order.products.map((product, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell className="font-mono text-xs">{product.ean}</TableCell>
                                        <TableCell className="font-mono text-xs">{product.asin}</TableCell>
                                        <TableCell>{product.quantity}</TableCell>
                                        <TableCell>€{product.unit_price_eur.toFixed(2)}</TableCell>
                                        <TableCell>{product.weight_grams || 'N/A'}</TableCell>
                                        <TableCell className="font-medium">${product.cogs?.toFixed(2) || 'N/A'}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                                {order.notes && (
                                  <p className="text-sm text-slate-500 mt-3">Notes: {order.notes}</p>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                    {sortedOrders.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                          No orders found. Create your first order or sync from Qogita.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="items" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Vue Produits</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date d'achat</TableHead>
                      <TableHead>Produit</TableHead>
                      <TableHead>Fournisseur</TableHead>
                      <TableHead>Order ID</TableHead>
                      <TableHead>EAN</TableHead>
                      <TableHead>ASIN</TableHead>
                      <TableHead>Lien commande</TableHead>
                      <TableHead className="text-right">QTT</TableHead>
                      <TableHead className="text-right">Prix unitaire €</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {flattenedLines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>{line.date}</TableCell>
                        <TableCell className="font-medium">{line.name}</TableCell>
                        <TableCell>{line.supplier}</TableCell>
                        <TableCell className="font-mono text-xs">{line.orderId}</TableCell>
                        <TableCell className="font-mono text-xs">{line.ean}</TableCell>
                        <TableCell className="font-mono text-xs">{line.asin}</TableCell>
                        <TableCell>
                          {line.link ? (
                            <a
                              href={line.link}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 underline text-sm"
                            >
                              Voir
                            </a>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className="text-right">{line.quantity}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatEuro(line.unitPrice)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {flattenedLines.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                          Aucune ligne produit. Synchronisez des commandes pour voir les détails.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </Layout>
  );
};
